import { PageTitleContext } from 'context/PageTitleContext';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BroadcastTable } from 'components/Broadcast/table';
import { BroadcastHeader } from 'components/Broadcast/header';
import { ApiConsumer, Backend, retrieveRecording } from 'api/ApiConsumer';
import { fetchCoverArtUrl } from 'hooks/useCoverArt';
import icon from 'assets/images/music-icon.svg';
import { API_ROUTES } from 'routes/api';
import { CrumbsContext } from 'context/CrumbsContext';
import { StackedLine } from 'components/Charts/stacked-line';
import { Doughnut } from 'components/Charts/doughnut';
import { ColumnChart } from 'components/Charts/column-chart';
import {
  normaliseTop5ArtistData,
  normaliseTop5LabelData,
  normalizePieChartData,
} from 'api/util';
import { MusicPlayerContext } from 'context/MusicPlayer';
import { NotificationContext } from 'context/NotificationContext';


export default function Broadcast() {
  const [data, setData] = useState([]);
  const [channel, setChannel] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [recording, setRecording] = useState({});
  let { setNotification } = useContext(NotificationContext);
  

  const [showTop5Artists, setShowTop5Artists] = useState(true);
  const [showTop5Labels, setShowTop5Labels] = useState(true);

  let { setTitle } = useContext(PageTitleContext);
  const { setPlaying } = useContext(MusicPlayerContext);
  setTitle('Broadcast Monitoring');

  let { id } = useParams();

  const playRecording = (item) => {
    if (recording.id === item.id) {
      setPlaying({
        music: recording,
      });
    } else if (item.file || item.recording) {
      setPlaying({
        music: item.file || item.recording,
      });
    } else {
      retrieveRecording(item, async (track) => {
        const img = await fetchCoverArtUrl(item.artist, item.title);
        Object.assign(track.music, { image: img || icon });
        if(item?.title){
          Object.assign(track.music, { title: `${item.title}`})
        }

        setRecording(track.music);
        setPlaying(track);
      },(err) => {
        setNotification({
          show: true,
          title: 'Playback Failed',
          message: `The recording is not available yet, try again later`,
          type: 'error',
        })
      },);
    }
  };

  function stream() {
    ApiConsumer.get(API_ROUTES.BROADCAST.CHANNEL(id))
      .then((res) => {
        setChannel(res.data);
        setCrumbs([
          { name: 'Monitoring', link: '/monitoring' },
          {
            name: 'Broadcast Monitoring',
            link: '/monitoring',
          },
          {
            name: res.data.name,
            link: `/monitoring/${res.data.id}`,
          },
        ]);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  let { setCrumbs } = useContext(CrumbsContext);

  useEffect(() => {
    setTitle('Broadcast Monitoring');
    setCrumbs([
      { name: 'Monitoring', link: '/monitoring' },
      {
        name: 'Broadcast Monitoring',
        link: '/monitoring',
      },
    ]);
    stream();
  }, []);

  useEffect(() => {
    setLoading(true);
    Backend.get(API_ROUTES.BROADCAST.FOR_CHANNEL(id, page))
      .then((res) => {
        setTotal(res.data.count);
        setData(res.data.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [id, page]);

  const renderTop5Section = () => {
    
    if (!showTop5Artists && !showTop5Labels) return <></>;

    return (
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 mb-5">
        {showTop5Artists ? (
          <div className="sm:col-span-1">
            <h3 className="mb-2 font-semibold profile-overview-title">
              Top 5 Artists
            </h3>
            <ColumnChart
              url={API_ROUTES.BROADCAST.CHANNEL_TOP_ARTISTS(id)}
              onDataLoad={(hasDataToShow) => setShowTop5Artists(hasDataToShow)}
              normaliseData={normaliseTop5ArtistData}
              options={{
                legend: { position: 'none' },
              }}
            />
          </div>
        ) : (
          <></>
        )}
        {showTop5Labels ? (
          <div className="sm:col-span-1">
            <h3 className="mb-2 font-semibold profile-overview-title">
              Top 5 Labels
            </h3>

            <ColumnChart
              url={API_ROUTES.BROADCAST.CHANNEL_TOP_LABELS(id)}
              onDataLoad={(hasDataToShow) => setShowTop5Labels(hasDataToShow)}
              normaliseData={normaliseTop5LabelData}
              options={{
                legend: { position: 'none' },
              }}
            />
          </div>
        ) : (
          <></>
        )}
      </section>
    );
  };

  return (
    <>
      <div className="min-h-full px-5 py-5 mt-0 mb-16">
        <BroadcastHeader broadcast={channel} />
        <section className="grid grid-cols-1 xl:grid-cols-1 2xl:grid-cols-4 gap-4 mt-5">
          <div className="col-span-4">
            <h3 className="mb-2 font-semibold profile-overview-title">
              Recognition by Month
            </h3>
            <StackedLine
              url={API_ROUTES.BROADCAST.REPORTS.COMPARISON.BY_MONTH_PER_STATION(
                id,
              )}
              title={'right'}
            />
          </div>
          <div className="col-span-2">
            <h3 className="mb-2 font-semibold profile-overview-title">
              Unrecognised vs Recognised
            </h3>
            <Doughnut
              url={API_ROUTES.BROADCAST.REPORTS.COMPARISON.ALL_BY_STATION(id)}
              normaliseData={normalizePieChartData}
              colors={["#f72585", "#5fa83f"]}
            />
          </div>
          <div className="col-span-2">
            <h3 className="mb-2 font-semibold profile-overview-title">
              International vs National
            </h3>
            <Doughnut
              url={API_ROUTES.BROADCAST.REPORTS.COMPARISON.LOCATION_BY_STATION(id)}
              normaliseData={normalizePieChartData}
            />
          </div>
        </section>
        {renderTop5Section()}
        <h3 className="mb-2 font-semibold profile-overview-title mt-5">
          Current Month Results
        </h3>
        <BroadcastTable
          onPlay={playRecording}
          data={data}
          loading={loading}
          currentPage={page}
          setPage={setPage}
          total={total}
          loadRecording={true}
        />
      </div>
    </>
  );
}
